/**
 * Iter-52.52 — Standalone Exercises screen.
 *
 * Reached via the compact "📝 Exercises / अभ्यास (N) — Open" CTA on
 * the Reading page. Hosts the full ExercisesPanel (sections +
 * Answer Key) with a persistent "← Back to reading / वापस पढ़ें"
 * bar at the top. Keeps the reading passage light while giving
 * exercises the full-screen breathing room the user asked for.
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import { ExercisesPanel } from '../components/ExercisesPanel';
import { stripMarkers, parseMatchExercise } from '../lib/textMarkers';

export default function ExercisesScreen() {
  const { bookId, pageId } = useParams();
  const nav = useNavigate();
  const { activeChild } = useApp();
  const [book, setBook] = useState(null);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/books/${bookId}`);
        if (!cancelled) setBook(data);
      } catch {
        if (!cancelled) setErrored(true);
      }
    })();
    return () => { cancelled = true; };
  }, [bookId]);

  const page = book?.pages?.find(p => p.page_id === pageId) || null;
  const rawText = page?.text || '';
  const parsedMatch = parseMatchExercise(rawText);
  const matchBlockPayload = parsedMatch.isMatch ? {
    instruction: parsedMatch.instruction,
    left: parsedMatch.left,
    right: parsedMatch.right,
    left_images: parsedMatch.leftImages || [],
  } : null;
  const cleanPassage = stripMarkers(rawText).replace(/^##\s*/gm, '').trim();
  const gender = activeChild?.voice_gender || 'female';
  const language = (book?.language || 'en').toLowerCase().startsWith('hi') ? 'hi' : 'en';
  const hiFont = language === 'hi' ? 'font-hindi' : '';

  return (
    <div className="min-h-screen bg-[#FFF8F1]">
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200">
        <div className="max-w-md mx-auto px-3 py-2 flex items-center gap-2">
          <button
            type="button"
            data-testid="exercises-back-btn"
            onClick={() => nav(`/read/${bookId}/${pageId}`)}
            className="inline-flex items-center gap-1.5 text-sm font-extrabold text-[#5B8DEF] hover:text-[#3577B8]">
            <ChevronLeft size={18} strokeWidth={3}/>
            Back to reading / वापस पढ़ें
          </button>
        </div>
      </header>
      <main className="px-3 pb-24">
        {!book && !errored && (
          <div className="max-w-md mx-auto mt-8 flex items-center gap-2 text-sm text-slate-500">
            <Loader2 size={16} className="animate-spin"/> Loading exercises…
          </div>
        )}
        {errored && (
          <div className="max-w-md mx-auto mt-8 text-sm text-red-500 font-bold">
            Couldn't load this book. Please go back and try again.
          </div>
        )}
        {page && (
          <ExercisesPanel
            bookId={bookId}
            pageId={pageId}
            passage={cleanPassage}
            language={language}
            hiFont={hiFont}
            childId={activeChild?.child_id}
            matchBlock={matchBlockPayload}
            voiceGender={gender}
            pageImage={(() => {
              const src = page.image_base64 || page.image_url || '';
              if (!src) return null;
              // Iter-52.55.1 — handle three shapes: (a) raw base64
              // string (wrap in data URL); (b) absolute http/https
              // URL (pass through); (c) backend-relative path like
              // `/api/uploads/pages/…` (prefix with backend origin so
              // the <img> tag doesn't hit the frontend origin and 404).
              if (src.startsWith('data:') || src.startsWith('http')) return src;
              if (src.startsWith('/')) {
                const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
                return `${base}${src}`;
              }
              return `data:image/jpeg;base64,${src}`;
            })()}
          />
        )}
      </main>
    </div>
  );
}
